import { ApiError } from '@/lib/api';

/**
 * WHAT THE TILL SAYS WHEN A CHECKOUT IS REFUSED.
 *
 * Owner, 2026-09-20: "using the owner account i cannot do full 100% discount it
 * tells me that some field are missing." That sentence is the server's
 * `validation_failed` headline — "Some fields need fixing." — and it was the
 * ONLY thing the drawer showed, because the catch kept `caught.message` and
 * dropped `caught.details` on the floor.
 *
 * The details were always there. ZodPipe keys every issue by its field path
 * precisely so the screen can point at the offending input, and every other
 * form in this app renders them; the till was the one place that threw them
 * away, which is why a refusal at the counter could not be diagnosed by the
 * person standing at it — or, afterwards, by anyone reading a report of it.
 *
 * Field paths are wire names (`buckets.time.percent`, `discountPercent`), not
 * labels, and deliberately so. A cashier reading one can say which box they
 * typed in; a translated label could not name a nested bucket at all, and the
 * alternative on the table was another sentence that says nothing.
 */
export function checkoutFailureMessage(caught: unknown, fallback: string): string {
  if (!(caught instanceof ApiError)) {
    return caught instanceof Error && caught.message ? caught.message : fallback;
  }

  const headline = caught.message || fallback;
  if (!Array.isArray(caught.details)) return headline;

  const named = caught.details
    .map((detail) => {
      if (typeof detail !== 'object' || detail === null) return '';
      const { field, message } = detail as { field?: unknown; message?: unknown };
      const label = typeof field === 'string' && field && field !== '(root)' ? field : '';
      const because = typeof message === 'string' ? message.trim() : '';
      // A detail with a field and no message still names the input, which is
      // the half that was missing; a message with no field still says what is
      // wrong. Only an empty detail contributes nothing.
      return [label, because].filter(Boolean).join(': ');
    })
    .filter(Boolean);

  return named.length > 0 ? `${headline} — ${named.join('; ')}` : headline;
}
